import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './unwrap';

/* eslint-disable @typescript-eslint/no-explicit-any -- the constraint below is a
   structural probe; concrete element types are recovered by inference per call. */

/**
 * Typed fetch closures + an invalidation `key` describing one REST resource.
 * Closures are passed in (never URL paths) so the openapi-typescript path typing
 * survives the indirection; the factory owns only the react-query wiring axis
 * (ADR 0012). Every `*` is optional — only the closures you supply yield hooks.
 */
export interface ResourceConfig<TDetail, TList, TCreate, TUpdate, TListParams> {
  /** Query-key prefix. Every list page and detail key nests under it, so one
   *  invalidate of this prefix wipes all param-encoded pages at once. */
  key: readonly unknown[];
  list?: (params?: TListParams) => Promise<TList>;
  get?: (id: string) => Promise<TDetail>;
  create?: (input: TCreate) => Promise<TDetail>;
  update?: (args: TUpdate) => Promise<TDetail>;
  remove?: (id: string) => Promise<void>;
}

/** A callable closure of any shape — the presence probe for conditional hooks. */
type AnyFn = (...args: never[]) => unknown;

/** The widest config the factory accepts; concrete types are inferred per call. */
type AnyConfig = ResourceConfig<any, any, any, any, any>;

// Recover each element type from whichever closure the caller supplied.
type ListResult<C> = C extends { list: (params?: any) => Promise<infer R> } ? R : never;
type ListParams<C> = C extends { list: (params?: infer P) => any } ? P : never;
type DetailResult<C> = C extends { get: (id: string) => Promise<infer R> }
  ? R
  : C extends { create: (input: any) => Promise<infer R> }
    ? R
    : C extends { update: (args: any) => Promise<infer R> }
      ? R
      : never;
type CreateInput<C> = C extends { create: (input: infer I) => any } ? I : never;
type UpdateArgs<C> = C extends { update: (args: infer A) => any } ? A : never;

interface ResourceKeys<C> {
  all: readonly unknown[];
  list: (params?: ListParams<C>) => readonly unknown[];
  detail: (id: string) => readonly unknown[];
}

/**
 * The hooks + key surface a resource exposes. Each hook is present only when its
 * closure was supplied — `C extends { list: AnyFn }` resolves to the hook type
 * when `list` is on the config, `undefined` otherwise.
 */
export interface Resource<C> {
  keys: ResourceKeys<C>;
  /** Returns a function that invalidates the whole `key` prefix (all pages). */
  useInvalidate: () => () => Promise<void>;
  useList: C extends { list: AnyFn }
    ? (params?: ListParams<C>) => UseQueryResult<ListResult<C>, ApiError>
    : undefined;
  useDetail: C extends { get: AnyFn }
    ? (id: string, enabled?: boolean) => UseQueryResult<DetailResult<C>, ApiError>
    : undefined;
  useCreate: C extends { create: AnyFn }
    ? () => UseMutationResult<DetailResult<C>, ApiError, CreateInput<C>>
    : undefined;
  useUpdate: C extends { update: AnyFn }
    ? () => UseMutationResult<DetailResult<C>, ApiError, UpdateArgs<C>>
    : undefined;
  useRemove: C extends { remove: AnyFn }
    ? () => UseMutationResult<void, ApiError, string>
    : undefined;
}

/**
 * Build a resource's react-query hooks from typed fetch closures. The
 * backend-contract axis (the closures) stays per-module and is passed in; this
 * factory owns only the query-key / invalidation / mutation ribbon shared by
 * every CRUD module.
 */
export function createResource<const C extends AnyConfig>(config: C): Resource<C> {
  const keys = {
    all: config.key,
    list: (params?: unknown) => [...config.key, 'list', params ?? null] as const,
    detail: (id: string) => [...config.key, 'detail', id] as const,
  };

  function useInvalidate() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: keys.all });
  }

  const { list, get, create, update, remove } = config;

  return {
    keys,
    useInvalidate,
    useList: list
      ? (params?: unknown) => useQuery({ queryKey: keys.list(params), queryFn: () => list(params) })
      : undefined,
    useDetail: get
      ? (id: string, enabled = true) =>
          useQuery({ queryKey: keys.detail(id), queryFn: () => get(id), enabled })
      : undefined,
    useCreate: create
      ? () => {
          const invalidate = useInvalidate();
          return useMutation({ mutationFn: create, onSuccess: () => invalidate() });
        }
      : undefined,
    useUpdate: update
      ? () => {
          const invalidate = useInvalidate();
          return useMutation({ mutationFn: update, onSuccess: () => invalidate() });
        }
      : undefined,
    useRemove: remove
      ? () => {
          const invalidate = useInvalidate();
          return useMutation({ mutationFn: remove, onSuccess: () => invalidate() });
        }
      : undefined,
  } as unknown as Resource<C>;
}
